import { Controller } from "@hotwired/stimulus"
import { createTurboDataTable, destroyTurboDataTable } from "lib/datatables_turbo"

// Index:  data-controller="service-notifications"
//         data-service-notifications-ajax-url-value="service_notifications"
// Form:   data-controller="service-notifications"

export default class extends Controller {
  static targets = ["table", "kindGroup", "accountId", "datepicker", "fileField", "fileName"]

  static values = {
    ajaxUrl: String,
  }

  connect() {
    if (this.hasTableTarget) this.#initDataTable()
    if (this.hasDatepickerTarget) this.#initDatepicker()
    if (this.hasKindGroupTarget) this.#syncAccountIdVisibility()
  }

  disconnect() {
    const confirmModal = document.getElementById("service-notifications-confirm")
    if (confirmModal) $(confirmModal).modal("hide")
    if (this.dataTable) {
      this.dataTable = destroyTurboDataTable(this.dataTable)
    }
    if (this.hasDatepickerTarget) {
      $(this.datepickerTarget).datetimepicker("destroy")
    }
  }

  openBulkModal(event) {
    event.preventDefault()
    $("#add-private-notification").modal("show")
  }

  kindChanged() {
    this.#syncAccountIdVisibility()
  }

  fileFieldChanged = () => {
    if (!this.hasFileNameTarget) return
    const value = (this.fileFieldTarget.value || "").replace(/.*(\/|\\)/, "")
    this.fileNameTarget.value = value
  }

  deleteNotification(event) {
    event.preventDefault()
    event.stopImmediatePropagation()

    const url = event.currentTarget.getAttribute("href")
    const title = event.currentTarget.dataset.notificationTitle
    this.#confirmDelete(title).then((confirmed) => {
      if (!confirmed) return
      this.#destroyNotification(url)
    })
  }

  // Private

  #syncAccountIdVisibility() {
    const checked = this.kindGroupTarget.querySelector("input:checked")
    this.accountIdTarget.style.display = checked && checked.value === "general" ? "none" : ""
  }

  #initDataTable() {
    const baseUrl = this.ajaxUrlValue

    this.dataTable = createTurboDataTable(this.tableTarget, {
      dom: '<"H"lfr>t<"F"<"toolbar">>',
      scrollX: true,
      bPaginate: false,
      order: [0, "asc"],
      stateSave: true,
      serverSide: true,
      processing: true,
      searchDelay: 2000,
      ajax: { url: baseUrl },
      columnDefs: [
        {
          targets: 0,
          createdCell(cell, cellData) {
            const id = cellData[0]
            const title = cellData[1]
            $(cell).html(`<a href="${baseUrl}/${id}/edit">${title}</a>`)
          },
        },
        {
          targets: 7,
          createdCell(cell, cellData, rowData) {
            const title = Array.isArray(rowData[0]) ? rowData[0][1] : ""
            const link = document.createElement("a")
            link.href = `${baseUrl}/${cellData}`
            link.className = "btn btn-primary btn-xs"
            link.dataset.action = "click->service-notifications#deleteNotification"
            if (title) link.dataset.notificationTitle = title
            link.innerHTML = '<i class="glyphicon glyphicon-trash"></i>'
            cell.replaceChildren(link)
          },
        },
        {
          targets: [0, 1, 2, 3, 4, 5, 7],
          searchable: false,
        },
      ],
    })
  }

  #destroyNotification(url) {
    const csrf = document.querySelector("meta[name='csrf-token']")?.content

    fetch(url, {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": csrf || "",
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) throw new Error("Delete failed")
        this.dataTable?.ajax.reload(null, false)
      })
      .catch(() => {
        if (typeof window.show_error === "function") {
          window.show_error("An error occurred during request")
        }
      })
  }

  #confirmDelete(title) {
    return new Promise((resolve) => {
      const modal = document.getElementById("service-notifications-confirm")
      if (!modal) {
        resolve(window.confirm("Are you sure?"))
        return
      }

      let settled = false
      const $modal = $(modal)
      const nameEl = modal.querySelector("[data-confirm-name]")
      if (nameEl) nameEl.textContent = title || "this notification"

      const finish = (value) => {
        if (settled) return
        settled = true
        $modal.off(".snConfirm")
        $modal.modal("hide")
        resolve(value)
      }

      $modal.off(".snConfirm")
      $(modal.querySelector("[data-confirm-ok]")).on("click.snConfirm", () => finish(true))
      $modal.on("hidden.bs.modal.snConfirm", () => finish(false))
      $modal.modal("show")
    })
  }

  #initDatepicker() {
    $(this.datepickerTarget).datetimepicker({
      format: "Y-m-d H:i:s",
      lang: "en",
      step: 1,
    })
  }
}
